import { type FC } from 'react';
import Button from '@components/Button';
import { ModalIcon } from '@components/ModalIcon';
import { Show, ShowFirstMatching } from '@components/Show';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import type { EntityData } from '@ContractBuilder/types';
import ContractValidationChecks from '@features/contract-validation/components/ContractValidationChecks';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import type { BlockError, ContractError } from '@root/@types/types';
import clsx from 'clsx';

import { ModalContentSkeleton } from '../components/ModalContentSkeleton';
import type { Config } from '../utils/getConfig';

interface ConfirmStatusFirmOrderModalProps {
  isLoading: boolean;
  onClose: () => void;
  blockErrors: BlockError[];
  contractErrors: { type: ContractError; error: Record<string, string>[] }[];
  submission?: EntityData;
  config: Config;
  hasLoadedSubmission: boolean;
}

export const ConfirmStatusFirmOrderModal: FC<ConfirmStatusFirmOrderModalProps> = ({
  onClose,
  isLoading,
  blockErrors,
  contractErrors,
  submission,
  config,
  hasLoadedSubmission,
}) => {
  return (
    <Dialog open={true} as="div" className={clsx('relative', MODAL_Z_INDEX)} onClose={onClose}>
      <div className="bg-info-500/75 fixed inset-0" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <DialogPanel className="relative inline-block max-w-[600px] transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:p-6 sm:align-middle">
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-offset-2 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <XIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <ShowFirstMatching>
                <Show when={!hasLoadedSubmission}>
                  <ModalContentSkeleton includeValidationChecks={config.includeValidationChecks} />
                </Show>
                <Show when={hasLoadedSubmission}>
                  <>
                    <ModalIcon variant={config.icon} />
                    <div className="mt-3 flex-1 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <DialogTitle as="h3" className="text-info-900 text-lg leading-6 font-medium">
                        {config.title}
                      </DialogTitle>
                      <div className="mt-3">
                        {config.descriptions.map((description) => (
                          <p className="text-info-500 mt-2 text-sm first:mt-0" key={description}>
                            {description}
                          </p>
                        ))}
                        {config.includeValidationChecks && (
                          <div className="mt-8">
                            <h3 className="text-info-900 mb-3 text-lg leading-6 font-medium">
                              Contract validation checks
                            </h3>
                            <ContractValidationChecks
                              blockErrors={blockErrors}
                              className="h-[312px] py-4"
                              contractErrors={contractErrors}
                              hasLoadedSubmission={hasLoadedSubmission}
                              submission={submission}
                            />
                            <hr />
                          </div>
                        )}
                      </div>
                      {config.includeFooter && (
                        <div className="mt-3">
                          <p className="text-info-500 text-sm">
                            <b>Note.</b> If you choose to bypass validation, the contract will be marked with a list of
                            the missing mandatory datapoints.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                </Show>
              </ShowFirstMatching>
            </div>
            <div className="mt-8 items-center justify-end gap-x-2.5 sm:mt-8 sm:flex">
              {config.buttons.map((btn) => {
                return (
                  <Button
                    key={btn.text}
                    onClick={btn.handleClick}
                    className="h-9"
                    size="sm"
                    kind={btn.type}
                    isDisabled={!hasLoadedSubmission}
                    loading={btn.disableWhenLoading ? isLoading : false}
                  >
                    {btn.text}
                  </Button>
                );
              })}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
